import React, { useState } from 'react';
import { Input, Button, Modal, Spin, Tabs, Select, Radio, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  StepBackwardOutlined, CaretLeftOutlined,
  CaretRightOutlined, StepForwardOutlined,
  PlusOutlined, EditOutlined, DeleteOutlined,
  SaveOutlined, CloseOutlined, SearchOutlined,
} from '@ant-design/icons';
import { useBusinessCardForm } from './hooks/useBusinessCardForm';
import { F1031, F1032, F1033 } from '../../services/businessCardApi';
import './P1103Form.css';

const P1103Form: React.FC = () => {
  const {
    mode, loading, form, setField, zodiac,
    details1031, setDetails1031,
    details1032, setDetails1032,
    details1033, setDetails1033,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save, doDelete,
    quickSearch, recordPosition, recordCount,
  } = useBusinessCardForm();

  const [searchKey, setSearchKey] = useState('');
  const isEditing = mode === 'add' || mode === 'edit';

  /* ===================== Page1: 基本資料 ===================== */
  const page1Tab = (
    <div className="p1103-fields">
      <div className="p1103-section-title">基本資料</div>
      <div className="p1103-field-row">
        <label>姓名</label>
        <Input value={form.f103_11} onChange={(e) => setField('f103_11', e.target.value)}
          maxLength={20} disabled={!isEditing} style={{ width: 200 }} />
      </div>
      <div className="p1103-field-row">
        <label>英文姓名</label>
        <Input value={form.f103_12} onChange={(e) => setField('f103_12', e.target.value)}
          maxLength={50} disabled={!isEditing} style={{ width: 200 }} />
      </div>
      <div className="p1103-field-row">
        <label>生日</label>
        <Input value={form.f103_13 ? form.f103_13.substring(0, 10) : ''}
          onChange={(e) => setField('f103_13', e.target.value || null)}
          maxLength={8} disabled={!isEditing} style={{ width: 140 }} placeholder="YYYYMMDD" />
        <label>星座</label>
        <Input value={zodiac} disabled style={{ width: 100 }} />
      </div>
      <div className="p1103-field-row">
        <label>性別</label>
        <Radio.Group value={form.f103_14} onChange={(e) => setField('f103_14', e.target.value)}
          disabled={!isEditing}>
          <Radio value="男">男</Radio>
          <Radio value="女">女</Radio>
        </Radio.Group>
        <label style={{ marginLeft: 16 }}>婚姻</label>
        <Radio.Group value={form.f103_32} onChange={(e) => setField('f103_32', e.target.value)}
          disabled={!isEditing}>
          <Radio value="已婚">已婚</Radio>
          <Radio value="未婚">未婚</Radio>
        </Radio.Group>
      </div>
      <div className="p1103-field-row">
        <label>血型</label>
        <Input value={form.f103_15} onChange={(e) => setField('f103_15', e.target.value)}
          maxLength={6} disabled={!isEditing} style={{ width: 80 }} />
      </div>
      <div className="p1103-field-row">
        <label>國別</label>
        <Input value={form.f103_16} onChange={(e) => setField('f103_16', e.target.value)}
          maxLength={20} disabled={!isEditing} style={{ width: 200 }} />
      </div>
      <div className="p1103-field-row">
        <label>區域(州.省.市)</label>
        <Input value={form.f103_17} onChange={(e) => setField('f103_17', e.target.value)}
          maxLength={20} disabled={!isEditing} style={{ width: 200 }} />
      </div>
      <div className="p1103-field-row">
        <label>戶籍住址</label>
        <Input value={form.f103_18} onChange={(e) => setField('f103_18', e.target.value)}
          maxLength={80} disabled={!isEditing} style={{ width: 400 }} />
      </div>
      <div className="p1103-field-row">
        <label>通訊地址</label>
        <Input value={form.f103_19} onChange={(e) => setField('f103_19', e.target.value)}
          maxLength={80} disabled={!isEditing} style={{ width: 400 }} />
      </div>
      <div className="p1103-field-row">
        <label>郵遞區號</label>
        <Input value={form.f103_20} onChange={(e) => setField('f103_20', e.target.value)}
          maxLength={15} disabled={!isEditing} style={{ width: 120 }} />
      </div>

      <div className="p1103-section-title">聯絡資訊</div>
      <div className="p1103-field-row">
        <label>聯絡電話</label>
        <Input value={form.f103_21} onChange={(e) => setField('f103_21', e.target.value)}
          maxLength={50} disabled={!isEditing} style={{ width: 200 }} />
      </div>
      <div className="p1103-field-row">
        <label>行動電話1</label>
        <Input value={form.f103_22} onChange={(e) => setField('f103_22', e.target.value)}
          maxLength={50} disabled={!isEditing} style={{ width: 200 }} />
      </div>
      <div className="p1103-field-row">
        <label>行動電話2</label>
        <Input value={form.f103_221} onChange={(e) => setField('f103_221', e.target.value)}
          maxLength={50} disabled={!isEditing} style={{ width: 200 }} />
      </div>
      <div className="p1103-field-row">
        <label>呼叫器</label>
        <Input value={form.f103_23} onChange={(e) => setField('f103_23', e.target.value)}
          maxLength={16} disabled={!isEditing} style={{ width: 200 }} />
      </div>
      <div className="p1103-field-row">
        <label>傳真</label>
        <Input value={form.f103_24} onChange={(e) => setField('f103_24', e.target.value)}
          maxLength={50} disabled={!isEditing} style={{ width: 200 }} />
      </div>
      <div className="p1103-field-row">
        <label>電子郵件</label>
        <Input value={form.f103_25} onChange={(e) => setField('f103_25', e.target.value)}
          maxLength={30} disabled={!isEditing} style={{ width: 300 }} />
      </div>
      <div className="p1103-field-row">
        <label>來源</label>
        <Input value={form.f103_29} onChange={(e) => setField('f103_29', e.target.value)}
          maxLength={20} disabled={!isEditing} style={{ width: 200 }} />
      </div>
      <div className="p1103-field-row">
        <label>關係</label>
        <Select value={form.f103_31 || undefined} onChange={(v) => setField('f103_31', v || '')}
          disabled={!isEditing} style={{ width: 120 }} allowClear
          placeholder="選擇關係">
          <Select.Option value="朋友">朋友</Select.Option>
          <Select.Option value="同事">同事</Select.Option>
          <Select.Option value="客戶">客戶</Select.Option>
          <Select.Option value="供應商">供應商</Select.Option>
          <Select.Option value="親戚">親戚</Select.Option>
          <Select.Option value="其他">其他</Select.Option>
        </Select>
        <label style={{ marginLeft: 16 }}>提供人</label>
        <Input value={form.f103_37} onChange={(e) => setField('f103_37', e.target.value)}
          maxLength={16} disabled={!isEditing} style={{ width: 120 }} />
      </div>
    </div>
  );

  /* ===================== Page2: 公司職務/地址 ===================== */
  const f1032Columns: ColumnsType<F1032> = [
    { title: '序號', dataIndex: 'f1032_02', key: 'f1032_02', width: 162 },
    { title: '關係', dataIndex: 'f1032_11', key: 'f1032_11', width: 55 },
    { title: '姓名', dataIndex: 'f1032_12', key: 'f1032_12', width: 85 },
    { title: '生日', dataIndex: 'f1032_13', key: 'f1032_13', width: 99 },
    { title: '年齡', dataIndex: 'f1032_14', key: 'f1032_14', width: 68 },
    { title: '備註', dataIndex: 'f1032_15', key: 'f1032_15', width: 235 },
    { title: '公司郵件', dataIndex: 'f1032_16', key: 'f1032_16', width: 230 },
    { title: '英文公司名稱', dataIndex: 'f1032_021', key: 'f1032_021', width: 170 },
  ];

  const f1033Columns: ColumnsType<F1033> = [
    { title: '類別', dataIndex: 'f1033_11', key: 'f1033_11', width: 63 },
    { title: '地址', dataIndex: 'f1033_12', key: 'f1033_12', width: 304 },
    { title: '英文地址(類別)', dataIndex: 'f1033_13', key: 'f1033_13', width: 150 },
    { title: '地址', dataIndex: 'f1033_14', key: 'f1033_14', width: 131 },
  ];

  const page2Tab = (
    <div className="p1103-fields">
      <div className="p1103-section-title">公司職務</div>
      <Table<F1032>
        dataSource={details1032}
        columns={f1032Columns}
        rowKey={(r) => `${r.f1032_01}-${r.f1032_02}`}
        size="small"
        pagination={false}
        scroll={{ x: 900 }}
      />
      <div className="p1103-section-title" style={{ marginTop: 16 }}>公司地址</div>
      <Table<F1033>
        dataSource={details1033}
        columns={f1033Columns}
        rowKey={(r) => `${r.f1033_01}-${r.f1033_02}-${r.f1033_11}`}
        size="small"
        pagination={false}
        scroll={{ x: 650 }}
      />
    </div>
  );

  /* ===================== Page3: 親友/嗜好/備註 ===================== */
  const f1031Columns: ColumnsType<F1031> = [
    { title: '序號', dataIndex: 'f1031_02', key: 'f1031_02', width: 35 },
    { title: '關係', dataIndex: 'f1031_11', key: 'f1031_11', width: 72 },
    { title: '姓名', dataIndex: 'f1031_12', key: 'f1031_12', width: 224 },
    { title: '生日', dataIndex: 'f1031_13', key: 'f1031_13', width: 95,
      render: (v: string | null) => v ? v.substring(0, 10) : '' },
    { title: '年齡', dataIndex: 'f1031_14', key: 'f1031_14', width: 39 },
    { title: '備註', dataIndex: 'f1031_21', key: 'f1031_21', width: 203 },
  ];

  const page3Tab = (
    <div className="p1103-fields">
      <div className="p1103-section-title">重要親友</div>
      <Table<F1031>
        dataSource={details1031}
        columns={f1031Columns}
        rowKey={(r) => `${r.f1031_01}-${r.f1031_02}`}
        size="small"
        pagination={false}
        scroll={{ x: 670 }}
      />
      <div className="p1103-section-title" style={{ marginTop: 16 }}>其他資料</div>
      <div className="p1103-field-row">
        <label>嗜好</label>
        <Input value={form.f103_34} onChange={(e) => setField('f103_34', e.target.value)}
          maxLength={60} disabled={!isEditing} style={{ width: 400 }} />
      </div>
      <div className="p1103-field-row">
        <label>宗教信仰</label>
        <Input value={form.f103_35} onChange={(e) => setField('f103_35', e.target.value)}
          maxLength={20} disabled={!isEditing} style={{ width: 200 }} />
      </div>
      <div className="p1103-field-row">
        <label>備註</label>
        <Input.TextArea value={form.f103_36} onChange={(e) => setField('f103_36', e.target.value)}
          disabled={!isEditing} rows={3} style={{ width: 400 }} />
      </div>
    </div>
  );

  return (
    <Spin spinning={loading}>
      <div className="p1103-form">
        <h2>名片個人資料檔</h2>

        {/* Form-level: 姓名(readOnly) + 關係 */}
        <div className="p1103-search-bar">
          <label style={{ marginRight: 8 }}>姓名</label>
          <Input value={form.f103_01} onChange={(e) => setField('f103_01', e.target.value)}
            maxLength={50} disabled={mode !== 'add'} style={{ width: 200 }} />
          <label style={{ marginLeft: 16, marginRight: 8 }}>關係</label>
          <Select value={form.f103_31 || undefined} onChange={(v) => setField('f103_31', v || '')}
            disabled={!isEditing} style={{ width: 120 }} allowClear
            placeholder="選擇關係">
            <Select.Option value="朋友">朋友</Select.Option>
            <Select.Option value="同事">同事</Select.Option>
            <Select.Option value="客戶">客戶</Select.Option>
            <Select.Option value="供應商">供應商</Select.Option>
            <Select.Option value="親戚">親戚</Select.Option>
            <Select.Option value="其他">其他</Select.Option>
          </Select>
        </div>

        {/* Quick Search */}
        <div className="p1103-search-bar">
          <Input
            placeholder="輸入姓名搜尋"
            value={searchKey}
            onChange={(e) => setSearchKey(e.target.value)}
            onPressEnter={() => quickSearch(searchKey)}
            style={{ width: 200 }}
            disabled={isEditing}
          />
          <Button
            icon={<SearchOutlined />}
            onClick={() => quickSearch(searchKey)}
            disabled={isEditing}
          >
            搜尋
          </Button>
          <span className="p1103-record-info">
            {recordPosition} / {recordCount}
          </span>
        </div>

        {/* Navigation */}
        <div className="p1103-nav-bar">
          <Button icon={<StepBackwardOutlined />} onClick={navFirst} disabled={isEditing}>首筆</Button>
          <Button icon={<CaretLeftOutlined />} onClick={navPrev} disabled={isEditing}>前筆</Button>
          <Button icon={<CaretRightOutlined />} onClick={navNext} disabled={isEditing}>次筆</Button>
          <Button icon={<StepForwardOutlined />} onClick={navLast} disabled={isEditing}>末筆</Button>
        </div>

        {/* Form Tabs - 3 pages matching FoxPro */}
        <Tabs defaultActiveKey="1" items={[
          { key: '1', label: '基本資料', children: page1Tab },
          { key: '2', label: '公司職務/地址', children: page2Tab },
          { key: '3', label: '親友/其他', children: page3Tab },
        ]} />

        {/* Audit info */}
        <div className="p1103-fields">
          <div className="p1103-section-title">稽核資訊</div>
          <div className="p1103-field-row">
            <label>建立人員</label>
            <Input value={form.f103_91} disabled style={{ width: 120 }} />
            <label>建立日期</label>
            <Input value={form.f103_92 ? form.f103_92.substring(0, 10) : ''} disabled style={{ width: 140 }} />
            <label>異動人員</label>
            <Input value={form.f103_93} disabled style={{ width: 120 }} />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="p1103-actions">
          {!isEditing ? (
            <>
              <Button type="primary" icon={<PlusOutlined />} onClick={startAdd}>新增</Button>
              <Button icon={<EditOutlined />} onClick={startEdit} disabled={recordCount === 0}>修改</Button>
              <Button
                danger
                icon={<DeleteOutlined />}
                disabled={recordCount === 0}
                onClick={() => {
                  Modal.confirm({
                    title: '確認刪除',
                    content: `確定要刪除 ${form.f103_01} 嗎？`,
                    onOk: doDelete,
                  });
                }}
              >
                刪除
              </Button>
            </>
          ) : (
            <>
              <Button type="primary" icon={<SaveOutlined />} onClick={save}>存檔</Button>
              <Button icon={<CloseOutlined />} onClick={cancel}>還原</Button>
            </>
          )}
        </div>
      </div>
    </Spin>
  );
};

export default P1103Form;
